import { LINK_ERROR_CODE as E } from '../constants/enums';

/**
 * Error code → { copy, placement, retry, retryAfterFallback } (03-ui section 14).
 *
 * Quota and busy states are worded differently on purpose: `RATE_LIMITED`
 * says "you're going fast", the busy codes say "the server is busy".
 * `retryAfterFallback` (seconds) is used when `Retry-After` is unreadable
 * (the API is cross-origin and may not expose it, U77).
 */
export const PLACEMENT = Object.freeze({
  PAGE: 'page',
  BANNER: 'banner',
  FIELD: 'field',
  FORM_BANNER: 'form-banner',
  TOAST: 'toast',
  PANEL: 'panel',
  INLINE: 'inline',
  RANGE: 'range',
  PRINT: 'print',
  CALLOUT: 'callout',
  SILENT: 'silent',
  LOG: 'log',
});

export const GENERIC_COPY = 'Something went wrong. Try again.';
export const NETWORK_COPY = "Couldn't reach the server. Check your connection and retry.";
export const BUSY_RETRYING_COPY = 'The server is busy right now. Trying again…';
export const BUSY_COPY = 'The server is busy right now. Retry';
export const DEFAULT_RETRY_AFTER_SECONDS = 2;

const entry = (copy, placement, retry = false, retryAfterFallback = null) =>
  Object.freeze({ copy, placement, retry, retryAfterFallback });

export const LINK_ERRORS = Object.freeze({
  [E.FORBIDDEN_DEPARTMENT]: entry(
    'QR & Links is for Marketing and Executive. Ask an Executive if you need access.',
    PLACEMENT.PAGE
  ),
  [E.DEPARTMENT_LOOKUP_UNAVAILABLE]: entry(
    "We couldn't confirm your access just now. Retry",
    PLACEMENT.BANNER,
    true
  ),
  // No IP or export wording, ever (#21, U11).
  [E.IP_ACCESS_FORBIDDEN]: entry(GENERIC_COPY, PLACEMENT.LOG),
  [E.EXPORT_FORBIDDEN]: entry(GENERIC_COPY, PLACEMENT.LOG),
  [E.LINK_NOT_FOUND]: entry("We couldn't find link {CODE}.", PLACEMENT.PAGE),
  [E.CAMPAIGN_NOT_FOUND]: entry("We couldn't find this campaign.", PLACEMENT.PAGE),
  [E.SLUG_TAKEN]: entry('Another link already uses this name.', PLACEMENT.FIELD),
  [E.SLUG_IMMUTABLE]: entry("Custom names can't be changed once set.", PLACEMENT.FORM_BANNER),
  [E.STALE_UPDATE]: entry(
    "Someone else changed this. We've loaded their version. Re-apply your changes.",
    PLACEMENT.FORM_BANNER
  ),
  [E.CONCURRENT_UPDATE]: entry(
    'Someone else is changing this right now. Try again.',
    PLACEMENT.TOAST,
    true
  ),
  [E.CAMPAIGN_NAME_TAKEN]: entry('A campaign with this name already exists.', PLACEMENT.FIELD),
  [E.IDEMPOTENCY_IN_PROGRESS]: entry(
    "Your save may still be finishing. We'll refresh the list.",
    PLACEMENT.TOAST,
    true,
    DEFAULT_RETRY_AFTER_SECONDS
  ),
  [E.IDEMPOTENCY_KEY_REUSED]: entry(GENERIC_COPY, PLACEMENT.LOG),
  [E.DESTINATION_INVALID]: entry("That address isn't valid. Use a full http or https link.", PLACEMENT.FIELD),
  [E.DESTINATION_IS_TRACKED_LINK]: entry(
    "This is already a tracked link. Point to the final page instead.",
    PLACEMENT.FIELD
  ),
  // A form link whose form_id no longer exists (S4).
  [E.LEAD_FORM_NOT_FOUND]: entry("That lead form doesn't exist any more. Pick another form.", PLACEMENT.FIELD),
  [E.UTM_EXTERNAL_DOMAIN_UNCONFIRMED]: entry(
    "{HOST} isn't a FlexCellence site. Many outside sites ignore UTM tags and some break with them.",
    PLACEMENT.CALLOUT
  ),
  [E.UTM_TOKEN_INVALID]: entry(
    'Use letters, digits, dot, hyphen or underscore (up to 100).',
    PLACEMENT.FIELD
  ),
  [E.EXPIRES_IN_PAST]: entry('Pick a time in the future.', PLACEMENT.FIELD),
  [E.BULK_ENABLE_NOT_ALLOWED]: entry(GENERIC_COPY, PLACEMENT.LOG),
  [E.OPERATION_NOT_ALLOWED]: entry(GENERIC_COPY, PLACEMENT.LOG),
  [E.CONFIRM_REQUIRED]: entry(GENERIC_COPY, PLACEMENT.LOG),
  [E.DATE_RANGE_INVALID]: entry('End date must be on or after the start date.', PLACEMENT.RANGE),
  [E.DATE_RANGE_TOO_LONG]: entry('Pick a range of 366 days or fewer.', PLACEMENT.RANGE),
  [E.CURSOR_INVALID]: entry('', PLACEMENT.SILENT, true),
  [E.LABEL_TEMPLATE_UNKNOWN]: entry(
    "That template isn't available any more. Pick another.",
    PLACEMENT.PRINT
  ),
  [E.TOO_MANY_LABELS]: entry('Up to 300 labels per sheet.', PLACEMENT.PRINT),
  [E.RATE_LIMITED]: entry(
    "You're going a bit fast. Wait a few seconds and try again.",
    PLACEMENT.TOAST,
    true
  ),
  [E.RENDER_BUSY]: entry(BUSY_COPY, PLACEMENT.INLINE, true, 2),
  [E.ANALYTICS_BUSY]: entry(BUSY_COPY, PLACEMENT.PANEL, true, 2),
  [E.EXPORT_BUSY]: entry(BUSY_COPY, PLACEMENT.INLINE, true, 10),
  [E.MODIFY_TIMEOUT]: entry(BUSY_COPY, PLACEMENT.INLINE, true, 2),
  [E.QUERY_TIMEOUT]: entry(
    'This range took too long to load. Try a shorter range.',
    PLACEMENT.PANEL,
    true
  ),
});

/** Per-endpoint overrides where the documented wait differs (S6 RENDER_BUSY is 5 s). */
export const PRINT_SHEET_RENDER_BUSY_SECONDS = 5;
export const EXPORT_RATE_LIMITED_COPY = 'Up to 5 exports a minute.';

/**
 * Parse `Retry-After` (seconds or HTTP date) into seconds, or null.
 * @param {string|number|null|undefined} value
 * @param {number} [nowMs=Date.now()]
 * @returns {number|null}
 */
export function parseRetryAfter(value, nowMs = Date.now()) {
  if (value === null || value === undefined || value === '') return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const at = Date.parse(String(value));
  return Number.isFinite(at) ? Math.max(0, Math.ceil((at - nowMs) / 1000)) : null;
}

/**
 * Read an axios error from the tracker API into one plain shape.
 * Never throws: it is called from catch blocks and render paths.
 *
 * @param {any} error
 * @returns {{ code: string|null, status: number|null, field: string|null, details: any,
 *   message: string|null, retryAfterSeconds: number|null, isNetwork: boolean, isAbort: boolean }}
 */
export function readLinkError(error) {
  const isAbort = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
  const response = error?.response;
  const detail = response?.data?.detail;
  const isObjectDetail = detail && typeof detail === 'object' && !Array.isArray(detail);
  const headers = response?.headers || {};
  const rawRetry = typeof headers.get === 'function' ? headers.get('retry-after') : headers['retry-after'];
  return {
    code: isObjectDetail && typeof detail.code === 'string' ? detail.code : null,
    status: typeof response?.status === 'number' ? response.status : null,
    field: isObjectDetail ? detail.field ?? null : null,
    details: isObjectDetail ? detail.details ?? null : null,
    message: isObjectDetail ? detail.message ?? null : null,
    retryAfterSeconds: parseRetryAfter(rawRetry),
    isNetwork: !isAbort && !response,
    isAbort,
  };
}

/**
 * The documented entry for a code, or null.
 * @param {string|null} code
 */
export function linkErrorFor(code) {
  return (code && LINK_ERRORS[code]) || null;
}

/**
 * User-facing copy for any error. Unknown codes and network failures get the
 * generic network line; `{CODE}` is filled when a code is given.
 * @param {any} error - axios error, or a `readLinkError` result.
 * @param {{ linkCode?: string, host?: string }} [vars]
 * @returns {string}
 */
export function linkErrorCopy(error, vars = {}) {
  const parsed = error && 'isNetwork' in error && 'code' in error ? error : readLinkError(error);
  const known = linkErrorFor(parsed.code);
  if (!known) return parsed.isNetwork || !parsed.status ? NETWORK_COPY : GENERIC_COPY;
  const host = vars.host || parsed.details?.host || 'This site';
  return known.copy
    .replace('{CODE}', String(vars.linkCode || '').toUpperCase())
    .replace('{HOST}', String(host));
}

/**
 * Seconds to wait before retrying: the header when readable, else the code's
 * documented fallback, else 2 s (U77).
 * @param {{ code: string|null, retryAfterSeconds: number|null }} parsed
 * @returns {number}
 */
export function retryDelaySeconds(parsed) {
  if (typeof parsed?.retryAfterSeconds === 'number') return parsed.retryAfterSeconds;
  return linkErrorFor(parsed?.code)?.retryAfterFallback ?? DEFAULT_RETRY_AFTER_SECONDS;
}
